import { useAtomValue } from 'jotai';

import { DRAWER_TITLE_ID, Text, cn } from '@growtherapy/sprout-ui';

import {
  ClientInformationDrawerHeaderContentWrapper as ClientInformationDrawerHeaderConnected,
  ClientInformationDrawerHeaderContent,
} from '@grow-therapy-team/seedling-components';

import { DrawerHeader } from '../../components/DrawerHeader';
import { ParticipantStatus } from '../../types';
import { scheduledPatientInformationAtom } from '../schedule-preview/state';
import { useGetPatientByShortId } from '../useGetPatientByShortId';
import { getAppointmentClientInfoHeader, getAppointmentType } from './utils';

type ClientInformationDrawerHeaderProps = {
  drawerTitle: string;
  visitorStatus?: ParticipantStatus;
  onClickBackButton?: () => void;
};

export function ClientInformationHeaderTitleBlock({
  drawerTitle,
  isScheduledClient,
}: {
  drawerTitle: string;
  isScheduledClient: boolean;
}) {
  return (
    <div className='flex flex-col'>
      <div className='flex flex-grow items-center'>
        <span
          className='fs-exclude'
          data-dd-privacy='mask'
          data-dd-action-name='Client Name'
          data-testid='client-information.header.name'
        >
          <Text
            as='span'
            variant={isScheduledClient ? 'lg' : 'md'}
            className={cn('flex flex-col', 'rebrand:typography-subhead-small rebrand:text-text-inverse', {
              'font-medium': isScheduledClient,
            })}
            id={DRAWER_TITLE_ID}
          >
            {drawerTitle}
          </Text>
        </span>
      </div>
    </div>
  );
}
export function ClientInformationDrawerHeader({
  drawerTitle,
  visitorStatus,
  onClickBackButton,
}: ClientInformationDrawerHeaderProps) {
  const isScheduledClient = visitorStatus === ParticipantStatus.SCHEDULED;
  const titleBlock = (
    <ClientInformationHeaderTitleBlock drawerTitle={drawerTitle} isScheduledClient={isScheduledClient} />
  );

  return (
    <DrawerHeader>
      {onClickBackButton ? (
        <ClientInformationDrawerHeaderContent
          // Seedling header uses items-start; self-center aligns the title with the back button.
          drawerTitle={<div className='self-center'>{titleBlock}</div>}
          onClickBackButton={onClickBackButton}
        />
      ) : (
        <ClientInformationDrawerHeaderConnected drawerTitle={titleBlock} />
      )}
    </DrawerHeader>
  );
}

export function ClientInformationDrawerHeaderWrapper({
  selectedPatientShortId,
  visitorName,
  ...rest
}: Omit<ClientInformationDrawerHeaderProps, 'drawerTitle'> & {
  selectedPatientShortId: string;
  visitorName?: string;
}) {
  const { data: patientChartData } = useGetPatientByShortId(selectedPatientShortId);
  const scheduledPatientInformation = useAtomValue(scheduledPatientInformationAtom);

  const appointmentType = getAppointmentType(
    scheduledPatientInformation?.appointmentType,
    patientChartData?.patientChart?.lastAppointment,
  );

  return (
    <ClientInformationDrawerHeader
      {...rest}
      drawerTitle={getAppointmentClientInfoHeader(appointmentType, visitorName)}
    />
  );
}
